import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, UserPlus, Home, Target, CheckSquare, Calendar,
  MessageSquare, Radar, FileSignature, FolderOpen, BarChart3, Megaphone,
  Sparkles, Settings, Search, Bell, LogOut, Menu, X, Command, Building2, Wrench, CalendarCheck, PanelLeftClose, PanelLeftOpen, Moon, Sun,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./kit";
import GlobalSearch from "./GlobalSearch";
import AICopilot from "./AICopilot";
import WelcomeModal from "./WelcomeModal";
import api from "../lib/api";
import { titleCase } from "../lib/constants";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/contacts", label: "Contacts", icon: Users, badge: "contacts" },
  { to: "/contacts?segment=pros", label: "Professionals & Vendors", icon: Wrench, match: "segment=pros" },
  { to: "/leads", label: "Leads", icon: UserPlus, badge: "leads" },
  { to: "/properties", label: "Properties", icon: Home, badge: "properties" },
  { to: "/pipeline", label: "Pipeline", icon: Target, badge: "opportunities" },
  { to: "/tasks", label: "Tasks & Follow-Ups", icon: CheckSquare, badge: "tasks" },
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/agenda", label: "Agenda · Daily Planner", icon: CalendarCheck, master: true },
  { to: "/communications", label: "Communications", icon: MessageSquare, badge: "communications" },
  { to: "/prospecting", label: "Prospecting", icon: Radar },
  { to: "/transactions", label: "Transactions", icon: FileSignature, badge: "transactions" },
  { to: "/documents", label: "Documents", icon: FolderOpen, badge: "documents" },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/marketing", label: "Marketing", icon: Megaphone },
  { to: "/ai", label: "AI Assistant", icon: Sparkles },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [hideSide, setHideSide] = useState(() => localStorage.getItem("gas:sidebar") === "hidden");
  const [dark, setDark] = useState(() => localStorage.getItem("gas:theme") === "dark");
  useEffect(() => { document.documentElement.classList.toggle("dark", dark); localStorage.setItem("gas:theme", dark ? "dark" : "light"); }, [dark]);
  useEffect(() => { localStorage.setItem("gas:sidebar", hideSide ? "hidden" : "shown"); }, [hideSide]);

  const loadBadges = useCallback(async () => {
    try {
      const { data } = await api.get("/badges");
      setBadges(data);
    } catch {}
  }, []);

  useEffect(() => { loadBadges(); }, [loadBadges, loc.pathname]);
  useEffect(() => {
    let t;
    const onChange = () => { clearTimeout(t); t = setTimeout(loadBadges, 400); };
    window.addEventListener("gas:data-changed", onChange);
    const iv = setInterval(loadBadges, 60000);
    return () => { window.removeEventListener("gas:data-changed", onChange); clearInterval(iv); clearTimeout(t); };
  }, [loadBadges]);
  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  }, [loc.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const isActive = (item) => {
    const to = typeof item === "string" ? item : item.to;
    if (item.match) return loc.pathname === "/contacts" && loc.search.includes(item.match);
    if (to === "/contacts") return loc.pathname.startsWith("/contacts") && !loc.search.includes("segment=pros");
    return to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
  };
  const unread = notifs.filter((n) => !n.read).length;

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
        <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-lg shrink-0 p-1">
          <img src="/brand/akinita-mark.png" alt="AKÍNITA" className="w-full h-full object-contain" data-testid="sidebar-logo" />
        </div>
        <div className="min-w-0">
          <p className="font-display font-bold text-white text-base leading-tight tracking-wide">AKÍNITA</p>
          <p className="text-[10px] uppercase tracking-widest text-gold/80 font-semibold truncate">Real Estate Intelligence</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-1">
        {NAV.filter((item) => !item.master || user?.role === "master").map((item) => {
          const active = isActive(item);
          const count = item.badge ? badges[item.badge] : 0;
          return (
            <button
              key={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => { nav(item.to); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-[background-color,color] relative group ${
                active ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-gold" />}
              <item.icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-gold" : ""}`} />
              <span className="flex-1 text-left truncate">{item.label}</span>
              {count > 0 && (
                <span data-testid={`nav-count-${item.badge}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gold text-navy-900 min-w-[18px] text-center">{count}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar contact={{ first_name: user?.name?.split(" ")[0], last_name: user?.name?.split(" ")[1], picture: user?.picture }} size={36} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-[11px] text-gold/80 truncate">{titleCase(user?.role || "")}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-slate-400 hover:text-red-400 transition-colors p-1.5">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Desktop sidebar */}
      <aside className={`${hideSide ? "hidden" : "hidden lg:flex"} w-64 bg-navy-900 fixed inset-y-0 left-0 z-30`} data-testid="desktop-sidebar">{SidebarInner}</aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)} className="fixed inset-0 bg-black/50 z-40 lg:hidden" />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed inset-y-0 left-0 w-64 bg-navy-900 z-50 lg:hidden">{SidebarInner}</motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className={`flex-1 ${hideSide ? "" : "lg:ml-64"} min-w-0`}>
        {/* Header */}
        <header className="sticky top-0 z-20 glass border-b border-slate-200">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
            <button className="hidden lg:block p-2 text-slate-600 hover:text-navy-800" onClick={() => setHideSide((v) => !v)} title={hideSide ? "Show sidebar" : "Hide sidebar"} data-testid="sidebar-toggle">{hideSide ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}</button>
            <button
              data-testid="global-search-trigger"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 flex-1 max-w-md px-3.5 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-sm transition-colors"
            >
              <Search className="w-4 h-4" />
              <span className="flex-1 text-left">Search contacts, properties, deals…</span>
              <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200"><Command className="w-2.5 h-2.5" />K</kbd>
            </button>
            <div className="flex-1" />
            <button data-testid="theme-toggle" onClick={() => setDark((v) => !v)} title={dark ? "Light mode" : "Dark mode"} className="p-2 text-slate-600 hover:text-navy-800 transition-colors">{dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}</button>
            <button
              data-testid="ai-copilot-trigger"
              onClick={() => setAiOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-navy-800 text-white text-sm font-medium hover:bg-navy-700 transition-colors"
            >
              <Sparkles className="w-4 h-4 text-gold" /><span className="hidden sm:inline">AI Copilot</span>
            </button>
            <div className="relative">
              <button data-testid="notifications-btn" onClick={() => setNotifOpen((v) => !v)} className="relative p-2 text-slate-600 hover:text-navy-800 transition-colors">
                <Bell className="w-5 h-5" />
                {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />}
              </button>
              <AnimatePresence>
                {notifOpen && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-slate-100 font-display font-semibold text-navy-800">Notifications</div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifs.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">You're all caught up</p>}
                      {notifs.map((n) => (
                        <button key={n.id} data-testid={`notification-${n.id}`}
                          onClick={async () => { if (!n.read) { api.post(`/notifications/${n.id}/read`).catch(() => {}); setNotifs((l) => l.map((x) => (x.id === n.id ? { ...x, read: true } : x))); } if (n.link) { setNotifOpen(false); nav(n.link); } }}
                          className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-2.5 ${n.read ? "opacity-70" : ""}`}>
                          <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.kind === "score_up" ? "bg-emerald-500" : n.kind === "score_down" ? "bg-red-500" : n.kind === "digest" ? "bg-gold" : n.read ? "bg-transparent" : "bg-sky-500"}`} />
                          <span className="min-w-0">
                            <p className="text-sm font-medium text-navy-800">{n.title}</p>
                            {n.body && <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1600px]">{children}</main>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AICopilot open={aiOpen} onClose={() => setAiOpen(false)} />
      <WelcomeModal />
    </div>
  );
}
